export type ReleaseNotesState = { [id: string]: { dismissed: boolean } };

export interface ReleaseNote {
  id: string;
  content: string;
}

/**
 * Fetches all release note IDs from manifest
 */
export async function getReleaseNoteIds(): Promise<string[]> {
  try {
    const response = await fetch("/release-notes/manifest.json");
    const ids: string[] = await response.json();
    return ids;
  } catch (error) {
    console.error("Error fetching release note IDs:", error);
    return [];
  }
}

/**
 * Fetches release note content by ID (filename) and language
 */
export async function getReleaseNoteContent(id: string, language: string = "en"): Promise<string | null> {
  try {
    const response = await fetch(`/release-notes/${language}/${id}`);
    if (!response.ok) {
      // Fallback to English if language-specific file doesn't exist
      if (language !== "en") {
        const fallbackResponse = await fetch(`/release-notes/en/${id}`);
        if (fallbackResponse.ok) {
          return await fallbackResponse.text();
        }
      }
      return null;
    }
    return await response.text();
  } catch (error) {
    console.error(`Error fetching release note ${id} for language ${language}:`, error);
    return null;
  }
}

/**
 * Gets all release notes with their content for a specific language
 */
export async function getAllReleaseNotes(language: string = "en"): Promise<ReleaseNote[]> {
  const ids = await getReleaseNoteIds();
  const notes: ReleaseNote[] = [];

  for (const id of ids) {
    const content = await getReleaseNoteContent(id, language);
    if (content) {
      notes.push({ id, content });
    }
  }

  return notes.sort((a, b) => b.id.localeCompare(a.id)); // Sort newest first
}

/**
 * Gets the next release note to show (first non-dismissed) for a specific language
 */
export async function getNextReleaseNoteToShow(
  state: ReleaseNotesState,
  language: string = "en"
): Promise<ReleaseNote | null> {
  const notes = await getAllReleaseNotes(language);

  for (const note of notes) {
    if (!state[note.id]?.dismissed) {
      return note;
    }
  }

  return null;
}

